import { useCallback, useEffect, useState } from 'react';
import { api } from '@/api/client';
import { useAuth } from '@/auth/AuthContext';
import { SettingsBillingTab } from './billing/SettingsBillingTab';
import { AccountSessions } from './AccountSessions';
import './SettingsModal.css';

type Tier = 'free' | 'pro' | 'power' | 'enterprise' | string;

interface Plan {
  id: string;
  name: string;
  price: string;
  currency: string;
  billingCycle: string;
  tier: Tier;
  /** Marketing eyebrow within the plan's group (Lite/Pro/Ultra), or null. This
   *  is what the plan cards show; `tier` is the enforcement enum and disagrees
   *  with it (Core is tier 'pro' but sells as "Lite"). */
  tierLabel?: string | null;
  tokenLimit: number | null;
  slotCap: number | null;
  isDefault: boolean;
  visibility: string;
}

interface Subscription {
  id?: string;
  plan: Plan;
  [k: string]: unknown;
}

interface Usage {
  count: number;
  limit: number | null;
  exceeded: boolean;
  tier: Tier;
  /** When true, this user is metered on CREDITS: count/limit are the monthly
   *  pool; the daily fields are the per-day fairness cap. */
  creditsEnforced?: boolean;
  /** True when credits are the unit to DISPLAY (plan grants a budget), even if
   *  enforcement is off — then use the monthly + daily credit fields below. */
  creditsShown?: boolean;
  dailyCreditsUsed?: number;
  dailyCreditCap?: number | null;
  monthlyCreditsUsed?: number;
  monthlyCredits?: number | null;
  // Separate BrewwCode budget (independent of the Assist pool above).
  brewcodeAccess?: boolean;
  brewcodeDailyCreditsUsed?: number;
  brewcodeDailyCreditCap?: number | null;
  brewcodeMonthlyCreditsUsed?: number;
  brewcodeMonthlyCredits?: number | null;
  [k: string]: unknown;
}

type Tab = 'account' | 'billing' | 'apikeys' | 'usage';

interface Props {
  open: boolean;
  onClose(): void;
  initialTab?: Tab;
  /** Token Saver mode — fewer tool calls / no MCP composer LLM per turn. */
  tokenSaver?: boolean;
  /** Toggle Token Saver on/off. */
  onToggleTokenSaver?(): void;
}

function initials(name?: string | null, email?: string | null): string {
  const src = (name || email || '').trim();
  if (!src) return '?';
  return src[0]!.toUpperCase();
}

export function getDocsUrl(path: string): string {
  const landingBase = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'http://localhost:4010'
    : 'https://breww.ai';
  return `${landingBase}${path}`;
}

/**
 * Settings modal opened from the chat sidebar gear button.
 *
 * Three tabs — Account, Billing, Usage. Account info comes straight from
 * useAuth(); the Usage tab lazy-loads subscription + usage the first time the
 * modal opens. Plan selection lives on the dedicated /plans page (reached from
 * the Billing tab), and invoices / payment method live in the Billing tab.
 */
export function SettingsModal({ open, onClose, initialTab = 'account', tokenSaver = false, onToggleTokenSaver }: Props) {
  const { user } = useAuth();

  const [tab, setTab] = useState<Tab>(initialTab);

  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Data for the Usage tab. subscription is fetched too so the tier badge has a
  // fallback when the usage payload omits it.
  const loadUsage = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [me, use] = await Promise.all([
        api.get<{ subscription: Subscription | null }>('/api/v2/subscriptions/me'),
        api.get<{ usage: Usage }>('/api/v2/subscriptions/usage'),
      ]);
      setSubscription(me.subscription);
      setUsage(use.usage);
    } catch (e) {
      setError((e as Error).message || 'Could not load your usage details');
    } finally {
      setLoading(false);
    }
  }, []);

  // Reset tab + RE-load usage every time the modal opens. It used to load only
  // once ever (a `loaded` latch), so after chatting the Usage tab stayed frozen
  // at its first-ever values until a full page refresh reset the component. The
  // modal sits over the chat, so usage only changes between opens — re-fetching
  // per open keeps the numbers current without any polling.
  useEffect(() => {
    if (!open) return;
    setTab(initialTab);
    void loadUsage();
  }, [open, initialTab, loadUsage]);

  // Esc to close.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const currentPlan = subscription?.plan ?? null;
  const tier = usage?.tier ?? currentPlan?.tier ?? null;
  // Show the badge the plan is SOLD as, falling back to the enforcement tier.
  // These differ — Core is tier 'pro' but its card eyebrow reads "Lite" — so
  // rendering `tier` here labelled the same plan two different things.
  const tierBadge = currentPlan?.tierLabel ?? tier;

  // Credits are the user-facing unit whenever the plan grants them (display OR
  // enforce). Tokens stay the internal metering unit.
  const showCredits = !!(usage?.creditsShown || usage?.creditsEnforced);

  // ── ASSIST budget (the primary pool). In credit mode these are the Assist
  //    credit figures; in legacy token mode they are the token cap. ──
  const assistUsed = showCredits ? (usage?.monthlyCreditsUsed ?? 0) : (usage?.count ?? 0);
  const assistLimit = showCredits ? (usage?.monthlyCredits ?? null) : (usage?.limit ?? null);
  const assistPct =
    assistLimit != null && assistLimit > 0 ? Math.min(100, Math.round((assistUsed / assistLimit) * 100)) : 0;
  const assistExceeded = !!usage?.exceeded || (assistLimit != null && assistUsed >= assistLimit);

  // ── BREWWCODE budget (separate pool). Shown only when the plan includes it. ──
  const showBrewcode = !!usage?.brewcodeAccess;
  const bcUsed = usage?.brewcodeMonthlyCreditsUsed ?? 0;
  const bcLimit = usage?.brewcodeMonthlyCredits ?? null;
  const bcPct = bcLimit != null && bcLimit > 0 ? Math.min(100, Math.round((bcUsed / bcLimit) * 100)) : 0;
  const bcExceeded = bcLimit != null && bcUsed >= bcLimit;

  // What share of the monthly pool one day may consume — COMPUTED, not asserted,
  // so the explainer below can't claim a ratio this plan doesn't have (operators
  // can override either cap). null ⇒ no daily cap to explain, so no explainer.
  const dailySharePct =
    showCredits && usage?.dailyCreditCap != null && assistLimit != null && assistLimit > 0
      ? Math.round((usage.dailyCreditCap / assistLimit) * 100)
      : null;

  return (
    <div className="settings-overlay" onMouseDown={onClose}>
      <div
        className="settings-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Settings"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="settings-modal__head">
          <h2 className="settings-modal__title">Settings</h2>
          <button
            type="button"
            className="settings-modal__close"
            aria-label="Close settings"
            title="Close"
            onClick={onClose}
          >
            ×
          </button>
        </header>

        <div className="settings-tabs" role="tablist">
          {(['account', 'billing', /* 'apikeys', */ 'usage'] as Tab[]).map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={tab === t}
              className={`settings-tab${tab === t ? ' is-active' : ''}`}
              onClick={() => setTab(t)}
            >
              {t === 'account' ? 'Account' : t === 'billing' ? 'Billing' : t === 'apikeys' ? 'API Keys' : 'Usage'}
            </button>
          ))}
        </div>

        <div className="settings-body">
          {/* ── Account tab ──────────────────────────────────────── */}
          {tab === 'account' && (
            <>
              {user ? (
                <>
                  <div className="settings-account">
                    <span className="settings-account__avatar">
                      {initials(user.name, user.email)}
                    </span>
                    <div className="settings-account__info">
                      <div className="settings-account__name">{user.name || '—'}</div>
                      <div className="settings-account__row">
                        <span className="settings-account__label">Email</span>
                        <span className="settings-account__value">{user.email}</span>
                      </div>

                    </div>
                  </div>

                  {/* Session history / tracking — active login sessions. */}
                  <AccountSessions />

                  <div className="settings-policies-disclaimer">
                    Your account activity and credentials are managed in accordance with our{' '}
                    <a href={getDocsUrl('/docs/terms-of-service')} target="_blank" rel="noopener noreferrer">
                      Terms of Service
                    </a>{' '}
                    and{' '}
                    <a href={getDocsUrl('/docs/privacy-policy')} target="_blank" rel="noopener noreferrer">
                      Privacy Policy
                    </a>
                    . Please ensure compliance with our{' '}
                    <a href={getDocsUrl('/docs/acceptable-use')} target="_blank" rel="noopener noreferrer">
                      Acceptable Use Policy
                    </a>{' '}
                    and refer to our{' '}
                    <a href={getDocsUrl('/docs/cookie-policy')} target="_blank" rel="noopener noreferrer">
                      Cookie Policy
                    </a>{' '}
                    for details on active session cookies. If you need any assistance, visit our{' '}
                    <a href={getDocsUrl('/docs/help-support')} target="_blank" rel="noopener noreferrer">
                      Help & Support
                    </a>{' '}
                    or view our{' '}
                    <a href={getDocsUrl('/docs/iso-certification')} target="_blank" rel="noopener noreferrer">
                      ISO Certification
                    </a>
                    .
                  </div>
                </>
              ) : (
                <p className="settings-muted">Not signed in.</p>
              )}
            </>
          )}

          {/* ── Billing tab ──────────────────────────────────────── */}
          {tab === 'billing' && <SettingsBillingTab active />}

          {/* ── API Keys tab ─────────────────────────────────────── */}
          {/* {tab === 'apikeys' && <ApiKeysPanel active={tab === 'apikeys'} />} */}

          {/* ── Usage tab ────────────────────────────────────────── */}
          {tab === 'usage' && (
            <>
              {loading && !error && <p className="settings-muted">Loading…</p>}
              {error && <div className="settings-error">{error}</div>}

              {!loading && !error && usage && (
                <div className="settings-usage">
                  {/* ── Breww Assist budget ─────────────────────────── */}
                  <div className="settings-usage__head">
                    <span className="settings-usage__title">
                      {showCredits ? 'Breww Assist — credits (last 30 days)' : 'Tokens used (last 30 days)'}
                    </span>
                    {tierBadge && <span className={`settings-tier-badge tier-${tier}`}>{tierBadge}</span>}
                  </div>
                  <div className="settings-usage__count">
                    {assistUsed.toLocaleString()}{' '}
                    <span className="settings-usage__sep">/</span>{' '}
                    {assistLimit == null ? 'Unlimited' : assistLimit.toLocaleString()}{' '}
                    {showCredits && <span className="settings-usage__sep" style={{ fontWeight: 400 }}>credits</span>}
                  </div>
                  {showCredits && usage.dailyCreditCap != null && (
                    <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>
                      Today: {(usage.dailyCreditsUsed ?? 0).toLocaleString()} / up to {usage.dailyCreditCap.toLocaleString()} credits
                    </div>
                  )}
                  {assistLimit != null && (
                    <div className="settings-usage__bar">
                      <div
                        className={`settings-usage__seg seg-assist${assistExceeded ? ' is-over' : ''}`}
                        style={{ width: `${assistPct}%` }}
                      />
                    </div>
                  )}
                  {assistExceeded && (
                    <div className="settings-usage__warn">
                      You've reached your Assist {showCredits ? 'credit' : 'token'} limit. Upgrade to keep going.
                    </div>
                  )}

                  {/* ── BrewwCode budget (separate pool; only if the plan grants it) ── */}
                  {showBrewcode && (
                    <div style={{ marginTop: 18 }}>
                      <div className="settings-usage__head">
                        <span className="settings-usage__title">BrewwCode — credits (last 30 days)</span>
                      </div>
                      <div className="settings-usage__count">
                        {bcUsed.toLocaleString()}{' '}
                        <span className="settings-usage__sep">/</span>{' '}
                        {bcLimit == null ? 'Unlimited' : bcLimit.toLocaleString()}{' '}
                        <span className="settings-usage__sep" style={{ fontWeight: 400 }}>credits</span>
                      </div>
                      {usage.brewcodeDailyCreditCap != null && (
                        <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>
                          Today: {(usage.brewcodeDailyCreditsUsed ?? 0).toLocaleString()} / up to {usage.brewcodeDailyCreditCap.toLocaleString()} credits
                        </div>
                      )}
                      {bcLimit != null && (
                        <div className="settings-usage__bar">
                          <div
                            className={`settings-usage__seg seg-brewcode${bcExceeded ? ' is-over' : ''}`}
                            style={{ width: `${bcPct}%` }}
                          />
                        </div>
                      )}
                      {bcExceeded && (
                        <div className="settings-usage__warn">
                          You've used your BrewwCode credits for now. They renew with your plan — or upgrade for more.
                        </div>
                      )}
                    </div>
                  )}

                  {/* The rule tying the two numbers together, stated once rather
                      than repeated under each budget. Without it the daily and
                      monthly figures read as two unrelated allowances — which is
                      exactly how they were picked before (4,000/month vs
                      230/day) and why the maths never added up. */}
                  {dailySharePct != null && (
                    <p className="settings-usage__rule">
                      Your monthly pool is what the plan grants. The daily cap is a fair-use burst
                      limit — {dailySharePct}% of that pool — so it is never a separate allowance to
                      add up.
                    </p>
                  )}

                  {/* Token Saver — make Brew use fewer tool calls so the
                      remaining allowance lasts longer. Mirrors the in-chat
                      toggle (shared state), so it persists across both. */}
                  {onToggleTokenSaver && (
                    <div className="settings-saver">
                      <div className="settings-saver__text">
                        <span className="settings-saver__title">Token Saver</span>
                        <span className="settings-saver__desc">
                          Breww uses fewer tool calls so your tokens stretch further.
                        </span>
                      </div>
                      <button
                        type="button"
                        className={`settings-saver__switch${tokenSaver ? ' is-on' : ''}`}
                        role="switch"
                        aria-checked={tokenSaver}
                        aria-label="Token Saver"
                        onClick={onToggleTokenSaver}
                      >
                        <span className="settings-saver__knob" />
                      </button>
                    </div>
                  )}

                  <div className="settings-policies-disclaimer">
                    Your resource usage and token allocations are subject to our{' '}
                    <a href={getDocsUrl('/docs/acceptable-use')} target="_blank" rel="noopener noreferrer">
                      Acceptable Use Policy
                    </a>{' '}
                    and{' '}
                    <a href={getDocsUrl('/docs/terms-of-service')} target="_blank" rel="noopener noreferrer">
                      Terms of Service
                    </a>
                    . Please ensure your API interactions align with these guidelines. For assistance or questions regarding your limits, refer to{' '}
                    <a href={getDocsUrl('/docs/help-support')} target="_blank" rel="noopener noreferrer">
                      Help & Support
                    </a>
                    .
                  </div>
                </div>
              )}

              {!loading && !error && !usage && (
                <p className="settings-muted">No usage data available.</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
